import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, DownloadSimple, UploadSimple, ChartLine, FloppyDisk, MagnifyingGlass, CircleNotch, LinkedinLogo } from "@phosphor-icons/react";

/**
 * AddAnalyticsModal
 * Lists user's Published (and any other) posts with inline metric inputs.
 * Supports a per-row save, a "save all changed rows" bulk save,
 * plus CSV template download and CSV upload.
 */
export default function AddAnalyticsModal({ open, onClose, onSaved }) {
  const [posts, setPosts] = useState([]);
  const [rows, setRows] = useState({}); // {postId: {impressions,reactions,comments,saves}}
  const [dirty, setDirty] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkedinSyncing, setLinkedinSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("published"); // published | all

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/posts");
      const p = (r.data || []).filter((x) => !x.deleted_at);
      setPosts(p);
      const seed = {};
      for (const x of p) {
        seed[x.id] = {
          impressions: x.impressions || 0,
          reactions:   x.reactions || 0,
          comments:    x.comments || 0,
          saves:       x.saves || 0,
        };
      }
      setRows(seed);
      setDirty(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const filtered = useMemo(() => {
    let out = posts;
    if (filter === "published") {
      out = out.filter((p) => (p.status || "").toLowerCase() === "published");
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((p) => ((p.topic || "") + " " + (p.content || "")).toLowerCase().includes(s));
    }
    return out;
  }, [posts, filter, q]);

  const setField = (pid, key, val) => {
    const n = Math.max(0, parseInt(val || "0", 10) || 0);
    setRows((prev) => ({ ...prev, [pid]: { ...prev[pid], [key]: n } }));
    setDirty((prev) => new Set(prev).add(pid));
  };

  const saveRow = async (pid) => {
    const post = posts.find((p) => p.id === pid);
    if (!post) return;
    try {
      await api.put(`/posts/${pid}`, {
        topic:       post.topic || "",
        content:     post.content || "",
        pillar:      post.pillar || "",
        status:      post.status || "Idea",
        date_to_post:post.date_to_post || null,
        checklist:   post.checklist || [],
        team_member: post.team_member || "",
        media_type:  post.media_type || "",
        media_url:   post.media_url || "",
        first_comment: post.first_comment || "",
        priority:    post.priority || 3,
        ...rows[pid],
      });
      toast.success("Saved.");
      setDirty((prev) => { const n = new Set(prev); n.delete(pid); return n; });
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAllDirty = async () => {
    if (dirty.size === 0) { toast.info("Nothing changed yet."); return; }
    setSavingAll(true);
    try {
      const payload = { rows: [...dirty].map((pid) => ({ post_id: pid, ...rows[pid] })) };
      const r = await api.post("/analytics/metrics/bulk", payload);
      toast.success(`Saved ${r.data.updated} post${r.data.updated===1?'':'s'}.`);
      setDirty(new Set());
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingAll(false); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/analytics/metrics-template.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "brandloop-analytics-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded. Fill in metrics, then upload.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCsv = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/analytics/metrics-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Updated ${r.data.updated} post${r.data.updated===1?'':'s'} from CSV${r.data.skipped ? ` (${r.data.skipped} skipped)` : ""}.`);
      load();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const syncLinkedIn = async (file) => {
    if (!file) return;
    setLinkedinSyncing(true);
    setSyncReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/analytics/linkedin-sync-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSyncReport(r.data);
      const m = r.data.matched;
      if (m > 0) toast.success(`Matched ${m} post${m===1?'':'s'} from LinkedIn export.`);
      else toast.info("No matches found. Check the columns detected below.");
      load();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLinkedinSyncing(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto" style={{ background: "rgba(0,0,0,0.55)" }} data-testid="add-analytics-backdrop">
      <div className="card w-full max-w-5xl p-0 fade-up flex flex-col" data-testid="add-analytics-modal" style={{ maxHeight: "88vh" }}>
        <div className="flex items-start justify-between gap-4 p-6 pb-3 shrink-0">
          <div>
            <div className="eyebrow mb-1" style={{ color: "var(--accent-2)" }}><ChartLine size={12} weight="fill" style={{ display: "inline", marginRight: 4 }}/> Add analytics</div>
            <h3 className="font-serif text-2xl font-semibold">Log performance for each post.</h3>
            <p className="text-sm mt-1" style={{ color: "var(--ink-3)" }}>Enter what LinkedIn showed you. She uses it to weight the winning patterns.</p>
          </div>
          <button type="button" onClick={onClose} data-testid="add-analytics-close" className="p-2 rounded-full hover:bg-[color:var(--paper)]" aria-label="Close"><X size={16}/></button>
        </div>

        <div className="px-6 flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-3)" }}/>
            <input
              data-testid="add-analytics-search"
              className="input pl-8"
              placeholder="Search by topic or content…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="seg" role="tablist" aria-label="Filter">
            <button data-testid="filter-published" onClick={() => setFilter("published")} data-active={filter === "published"}>Published</button>
            <button data-testid="filter-all" onClick={() => setFilter("all")} data-active={filter === "all"}>All posts</button>
          </div>
          <div className="flex-1" />
          <button data-testid="download-template" onClick={downloadTemplate} className="btn btn-ghost text-xs py-1.5 px-3"><DownloadSimple size={13}/> CSV template</button>
          <label className="btn btn-ghost text-xs py-1.5 px-3 cursor-pointer" data-testid="upload-csv-label">
            {uploading ? <><CircleNotch size={13} className="animate-spin"/> Uploading…</> : <><UploadSimple size={13}/> Upload CSV</>}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="upload-csv-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { uploadCsv(f); e.target.value = ""; } }}
            />
          </label>
          <label
            className="btn text-xs py-1.5 px-3 cursor-pointer"
            data-testid="linkedin-sync-label"
            style={{ background: "#0A66C2", color: "#fff", border: "1px solid #0A66C2" }}
            title="Upload your LinkedIn analytics CSV export — we auto-match rows to your posts by hook + date."
          >
            {linkedinSyncing ? <><CircleNotch size={13} className="animate-spin"/> Syncing…</> : <><LinkedinLogo size={13} weight="fill"/> Sync from LinkedIn</>}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="linkedin-sync-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { syncLinkedIn(f); e.target.value = ""; } }}
            />
          </label>
        </div>

        {syncReport && (
          <div className="mx-6 mt-3 rounded-2xl p-4" data-testid="linkedin-sync-report" style={{ background: "var(--bg-soft)", border: "1px solid var(--line-2)" }}>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div className="eyebrow flex items-center gap-2" style={{ color: "var(--accent-2)" }}>
                <LinkedinLogo size={12} weight="fill"/> LinkedIn sync report
              </div>
              <button
                onClick={() => setSyncReport(null)}
                className="p-1.5 rounded-full hover:bg-white"
                title="Dismiss"
                data-testid="linkedin-sync-dismiss"
              ><X size={12}/></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-3">
              <div><div className="eyebrow mb-0.5">Matched</div><div className="font-mono text-lg" style={{ color: "#4a7c3f" }}>{syncReport.matched}</div></div>
              <div><div className="eyebrow mb-0.5">Unmatched</div><div className="font-mono text-lg" style={{ color: "var(--ink-3)" }}>{syncReport.unmatched}</div></div>
              <div className="col-span-2"><div className="eyebrow mb-0.5">Columns detected</div>
                <div className="text-[11px] font-mono truncate" title={JSON.stringify(syncReport.columns_detected)}>
                  {Object.entries(syncReport.columns_detected || {}).map(([k, v]) => `${k}→${v}`).join(" · ") || "—"}
                </div>
              </div>
            </div>
            {syncReport.unmatched > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer" style={{ color: "var(--ink-2)" }}>
                  {syncReport.unmatched} unmatched rows (click to review)
                </summary>
                <div className="mt-2 space-y-1 max-h-40 overflow-y-auto scroll-thin">
                  {(syncReport.unmatched_rows || []).map((r, i) => (
                    <div key={i} className="rounded-xl px-2 py-1.5 flex items-center gap-3" style={{ background: "var(--bg-elev)" }} data-testid={`unmatched-row-${i}`}>
                      <span className="truncate flex-1" title={r.preview}>{r.preview || "(no content)"}</span>
                      <span className="font-mono text-[10px]" style={{ color: "var(--ink-3)" }}>{r.date || ""}</span>
                      <span className="font-mono text-[10px]" style={{ color: "var(--ink-3)" }}>{r.impressions} imp</span>
                    </div>
                  ))}
                </div>
                <div className="text-[11px] mt-2" style={{ color: "var(--ink-3)" }}>
                  Tip: unmatched rows usually mean the post text differs. Add the post manually via Planner, then re-sync.
                </div>
              </details>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto scroll-thin px-6 mt-4">
          {loading ? (
            <div className="text-sm py-8 text-center" style={{ color: "var(--ink-3)" }}><CircleNotch size={16} className="animate-spin inline mr-2"/> Loading posts…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <div className="eyebrow mb-2">Nothing to show</div>
              <p className="text-sm max-w-md mx-auto" style={{ color: "var(--ink-3)" }}>
                {filter === "published" ? "No Published posts yet. Move some drafts to Published in the Planner, then come back." : "No posts match this search."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="analytics-posts-table">
              <thead className="sticky top-0 z-10" style={{ background: "var(--bg-elev)" }}>
                <tr className="text-left" style={{ color: "var(--ink-3)" }}>
                  <th className="pb-2 pr-3 font-normal eyebrow">Post</th>
                  <th className="pb-2 px-1 font-normal eyebrow text-right">Impressions</th>
                  <th className="pb-2 px-1 font-normal eyebrow text-right">Reactions</th>
                  <th className="pb-2 px-1 font-normal eyebrow text-right">Comments</th>
                  <th className="pb-2 px-1 font-normal eyebrow text-right">Saves</th>
                  <th className="pb-2 pl-2 font-normal eyebrow text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const isDirty = dirty.has(p.id);
                  const r = rows[p.id] || { impressions: 0, reactions: 0, comments: 0, saves: 0 };
                  const topic = (p.topic || (p.content || "").split("\n", 1)[0] || "(untitled)").slice(0, 90);
                  return (
                    <tr key={p.id} data-testid={`post-row-${p.id}`} className="align-top" style={{ borderTop: "1px solid var(--line)" }}>
                      <td className="py-3 pr-3">
                        <div className="text-sm font-medium truncate max-w-[380px]" title={topic}>{topic}</div>
                        <div className="text-[11px] mt-0.5 font-mono flex items-center gap-2" style={{ color: "var(--ink-3)" }}>
                          <span className="chip" style={{ padding: "1px 8px", fontSize: 10 }}>{p.status || "Idea"}</span>
                          {p.date_to_post && <span>{new Date(p.date_to_post).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
                        </div>
                      </td>
                      {["impressions", "reactions", "comments", "saves"].map((k) => (
                        <td key={k} className="py-3 px-1">
                          <input
                            data-testid={`row-${p.id}-${k}`}
                            type="number"
                            min="0"
                            className="input text-right font-mono"
                            style={{ padding: "6px 8px", fontSize: 12, width: 100 }}
                            value={r[k]}
                            onChange={(e) => setField(p.id, k, e.target.value)}
                          />
                        </td>
                      ))}
                      <td className="py-3 pl-2 text-right">
                        <button
                          onClick={() => saveRow(p.id)}
                          disabled={!isDirty}
                          data-testid={`save-row-${p.id}`}
                          className="btn btn-ghost text-xs py-1.5 px-3"
                          style={isDirty ? { color: "var(--accent-2)", borderColor: "var(--accent)" } : {}}
                        >
                          <FloppyDisk size={12}/> Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 flex items-center justify-between gap-3 shrink-0" style={{ borderTop: "1px solid var(--line)" }}>
          <div className="text-xs font-mono" style={{ color: "var(--ink-3)" }}>
            {dirty.size > 0 ? `${dirty.size} row${dirty.size === 1 ? "" : "s"} with unsaved changes` : "All caught up."}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-ghost text-xs py-2 px-4">Close</button>
            <button
              onClick={saveAllDirty}
              disabled={dirty.size === 0 || savingAll}
              data-testid="save-all-metrics"
              className="btn btn-accent text-xs py-2 px-4"
            >
              {savingAll ? <><CircleNotch size={13} className="animate-spin"/> Saving…</> : <><FloppyDisk size={13}/> Save all changes ({dirty.size})</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
